import axios, { AxiosError } from "axios";
import { supabase } from "./supabaseAPI";
import {
  AppError,
  createAppError,
  getErrorCodeFromStatus,
} from "@/types/errors";
import { stepUpManager } from "@shared/utils/stepUpManager";

// Event emitted when a 401 unauthorized error occurs
export const AUTH_ERROR_EVENT = "auth:unauthorized";

// Marker we set on the axios config when replaying a request after a
// successful step-up. Prevents an infinite loop if the replayed
// request also returns mfa_required (which would indicate a server
// misconfiguration, not a recoverable state).
const STEP_UP_REPLAY_FLAG = "_stepUpReplayed";

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
});

// Request interceptor - adds auth token
apiClient.interceptors.request.use(
  async (config) => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - transforms errors into AppError
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: string; message?: string }>) => {
    const status = error.response?.status;
    const serverMessage =
      error.response?.data?.error || error.response?.data?.message;
    const code = getErrorCodeFromStatus(status);

    // Step-up flow. The server signals "your token is AAL1 but this
    // route needs AAL2" with 401 + {error: "mfa_required"}. Open the
    // step-up dialog, wait for the user to verify, then replay the
    // original request. The request interceptor pulls a fresh session
    // each call, so the replay automatically carries the new AAL2
    // token. The replay-flag guards against an infinite loop if the
    // replayed request also comes back mfa_required (server bug).
    const cfg = error.config as
      | (typeof error.config & { [STEP_UP_REPLAY_FLAG]?: boolean })
      | undefined;
    if (
      status === 401 &&
      serverMessage === "mfa_required" &&
      cfg &&
      !cfg[STEP_UP_REPLAY_FLAG]
    ) {
      const verified = await stepUpManager.request();
      if (verified) {
        cfg[STEP_UP_REPLAY_FLAG] = true;
        return apiClient.request(cfg);
      }
      // User cancelled — fall through to the normal 401 handling so
      // the caller sees the error.
    }

    let appError: AppError;

    if (status === 401) {
      appError = createAppError(
        serverMessage || "Session expired. Please log in again.",
        401,
        code
      );
      // Emit event for auth context to handle logout.
      // Skip on mfa_required: that's a step-up signal, not a session
      // failure — the user is still validly authenticated at AAL1.
      if (serverMessage !== "mfa_required") {
        window.dispatchEvent(new CustomEvent(AUTH_ERROR_EVENT));
      }
    } else if (status === 403) {
      appError = createAppError(
        serverMessage || "You do not have permission to perform this action.",
        403,
        code
      );
    } else if (status === 404) {
      appError = createAppError(
        serverMessage || "The requested resource was not found.",
        404,
        code
      );
    } else if (status && status >= 500) {
      appError = createAppError(
        serverMessage || "A server error occurred. Please try again later.",
        status,
        code
      );
    } else {
      appError = createAppError(
        serverMessage || error.message || "An unexpected error occurred.",
        status,
        code
      );
    }

    return Promise.reject(appError);
  }
);

export default apiClient;
